import { z } from 'zod';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');

/**
 * SAV et qualité (CDC §12).
 *  - créé depuis une installation TERMINÉE par un admin, un superviseur ou un commercial ;
 *  - créé par un commercial (sans le droit de valider) : « À valider » avant toute attribution ;
 *  - motif prédéfini (liste administrable) + « Autre », photos et commentaire ;
 *  - attribution au technicien par une intervention SAV : même mécanisme accepter / refuser ;
 *  - clôture simple : cause catégorisée + commentaire ;
 *  - le technicien qui avait posé l'installation reste rattaché au SAV (statistiques qualité).
 */
export const SAV_STATUSES = ['pending_validation', 'open', 'in_progress', 'done', 'closed', 'rejected', 'cancelled'] as const;
export type SavStatus = (typeof SAV_STATUSES)[number];
export const SAV_STATUS_LABELS: Record<SavStatus, string> = {
  pending_validation: 'À valider',
  open: 'À planifier',
  in_progress: 'Intervention en cours',
  done: 'Intervention faite — à clôturer',
  closed: 'Clôturé',
  rejected: 'Refusé',
  cancelled: 'Annulé',
};
export const SAV_OPEN: SavStatus[] = ['pending_validation', 'open', 'in_progress', 'done'];

/** Actions utilisateur (`sav.validate`) ; le passage en cours / fait suit l'intervention (système). */
export const SAV_USER_TRANSITIONS: Record<string, { from: SavStatus[]; to: SavStatus; needs: 'none' | 'comment' | 'cause' }> = {
  validate: { from: ['pending_validation'], to: 'open', needs: 'none' },
  reject: { from: ['pending_validation'], to: 'rejected', needs: 'comment' },
  close: { from: ['open', 'in_progress', 'done'], to: 'closed', needs: 'cause' },
  cancel: { from: ['pending_validation', 'open'], to: 'cancelled', needs: 'comment' },
};
export type SavAction = keyof typeof SAV_USER_TRANSITIONS;

export interface SavRecord {
  id: string;
  version: number;
  updatedAt: string;
  reference: string | null;
  dossierId: string;
  territoryId: string;
  status: SavStatus;
  reasonId: string;
  description: string | null;
  /** technicien qui avait réalisé l'installation d'origine (qualité) */
  installerId: string | null;
  installerName: string | null;
  interventionId: string | null;
  createdById: string;
  createdByName: string;
  validatedByName: string | null;
  validatedAt: string | null;
  statusComment: string | null;
  causeId: string | null;
  closingComment: string | null;
  closedByName: string | null;
  closedAt: string | null;
  createdAt: string;
}

export const savCreateInput = z.object({
  id: uuid,
  dossierId: uuid,
  reasonId: uuid,
  description: z.string().trim().max(2000).nullish().transform((v) => v || null),
});
export type SavCreateInput = z.input<typeof savCreateInput>;

export const savTransitionInput = z.object({
  action: z.enum(['validate', 'reject', 'close', 'cancel']),
  comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
  causeId: uuid.nullish().transform((v) => v ?? null),
});
export type SavTransitionInput = z.input<typeof savTransitionInput>;

/* ------------------------------------------------------------------ tableau de bord qualité */

export interface QualityRow {
  technicianId: string;
  technicianName: string;
  /** poses / entretiens terminés sur la période (hors interventions SAV) */
  installs: number;
  /** SAV ouverts sur la période sur des installations de ce technicien */
  savs: number;
  /** taux de SAV (0–1), null sans pose */
  rate: number | null;
}
export interface QualityDashboard {
  from: string;
  to: string;
  totals: { installs: number; savs: number; rate: number | null };
  technicians: QualityRow[];
  byReason: { reasonId: string; label: string; count: number }[];
  byCause: { causeId: string | null; label: string; count: number }[];
}

export const savRate = (savs: number, installs: number) => (installs > 0 ? savs / installs : null);
export const formatRate = (r: number | null) => (r === null ? '—' : `${(r * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
